/**
 * TaskPanel — inline task details at the bottom of the Gantt view.
 * Edits are held locally until the user clicks Save changes.
 */

import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Paper,
  ActionIcon,
  Text,
  Box,
  Group,
  Transition,
  Button,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconX } from '@tabler/icons-react';
import type { ITaskStore } from '../../../stores/ITaskStore';
import type { TaskSnapshot } from '../../../stores/types';
import type { PredecessorLinkChange } from '../../../gantt/taskLinkDisplayUtils';
import { useTaskDetailsDraft } from '../../../hooks/useTaskDetailsDraft';
import { TaskDetailsFormBody } from './TaskDetailsFormBody';
import { TaskDetailsFormActions } from './TaskDetailsFormActions';
import { TaskModalBusyOverlay } from './TaskModalBusyOverlay';
import { TrackTaskConfirmControl } from './TrackTaskConfirmControl';

interface TaskPanelProps {
  store: ITaskStore;
  onClose?: () => void;
  onTaskUpdate?: (taskId: string, changes: Partial<TaskSnapshot>) => void;
  onPredecessorUpdate?: (taskId: string, change: PredecessorLinkChange) => void;
  resolvePredecessorWbsCode?: (taskId: string) => string | null;
  onTrackTask?: (taskId: string) => void | Promise<void>;
  trackTaskLoadingTaskId?: string | null;
  className?: string;
  /** Switch task details layout to the right drawer. */
  onSwitchToRightPanel?: () => void;
  /** When compare mode is on, show Current vs Baseline N in the form. */
  compareBaselineIndex?: number | null;
  /** Same gate as Gantt "Save to Server". */
  canMutate?: boolean;
  /** Increment to scroll Issues / Hindrances into view after open. */
  scrollToLinkedIssuesRequest?: number;
  /** Open predecessor task-tree picker for the selected task. */
  onPickPredecessors?: () => void;
  /** Open Assign users drawer for the selected task. */
  onOpenAssignUsers?: () => void;
  /** Assignees summary line for the selected task. */
  assigneesSummary?: string;
  /** Register draft mutators so GanttPage picker can update panel predecessors. */
  registerPredecessorDraftMutator?: (
    mutator: { getDraft: () => string; applyDraft: (value: string) => void } | null,
  ) => void;
}

export const TaskPanel: React.FC<TaskPanelProps> = ({
  store,
  onClose,
  onTaskUpdate,
  onPredecessorUpdate,
  resolvePredecessorWbsCode,
  onTrackTask,
  trackTaskLoadingTaskId,
  className,
  onSwitchToRightPanel,
  compareBaselineIndex = null,
  canMutate = true,
  scrollToLinkedIssuesRequest = 0,
  onPickPredecessors,
  onOpenAssignUsers,
  assigneesSummary,
  registerPredecessorDraftMutator,
}) => {
  const handleSaveSuccess = useCallback(() => {
    store.setSelectedTaskId(null);
    onClose?.();
  }, [store, onClose]);

  const {
    draft,
    isDirty,
    isSaving,
    readOnlyForNonLeaf,
    resolvedLocationValue,
    draftPredecessors,
    successorText,
    predecessorFieldLabel,
    successorFieldLabel,
    isLeafTask,
    updateDraftField,
    updateDraftQuantity,
    quantityErrors,
    quantityDirty,
    updateDraftPredecessors,
    commitDraftPredecessors,
    saveChanges,
    cancelChanges,
  } = useTaskDetailsDraft(store, {
    onApplyChanges: onTaskUpdate,
    onCommitPredecessorLinks: onPredecessorUpdate,
    resolvePredecessorWbsCode,
    onSaveSuccess: handleSaveSuccess,
  });

  useEffect(() => {
    if (!registerPredecessorDraftMutator) return;
    registerPredecessorDraftMutator({
      getDraft: () => draftPredecessors,
      applyDraft: (value: string) => {
        updateDraftPredecessors(value);
        commitDraftPredecessors(value);
      },
    });
    return () => registerPredecessorDraftMutator(null);
  }, [
    registerPredecessorDraftMutator,
    draftPredecessors,
    updateDraftPredecessors,
    commitDraftPredecessors,
  ]);

  const handleClose = useCallback(() => {
    if (isSaving) return;
    if (isDirty) {
      cancelChanges();
    }
    store.setSelectedTaskId(null);
    onClose?.();
  }, [store, onClose, isDirty, isSaving, cancelChanges]);

  const showTrackTask = useMemo(() => {
    if (!draft || !onTrackTask || !isLeafTask) return false;
    const rows = draft.unitActivityDetails;
    return !(Array.isArray(rows) && rows.length > 0);
  }, [draft, onTrackTask, isLeafTask]);

  const trackTaskLoading =
    draft != null &&
    trackTaskLoadingTaskId != null &&
    String(trackTaskLoadingTaskId) === String(draft.id);

  if (!draft) {
    return null;
  }

  return (
    <Transition mounted transition="slide-up" duration={200} timingFunction="ease">
      {(styles) => (
        <Paper
          shadow="md"
          p={0}
          withBorder
          className={className}
          style={{
            ...styles,
            borderTop: '2px solid var(--mantine-color-brand-6)',
            height: '100%',
            maxHeight: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Group
            justify="space-between"
            px="md"
            pt="md"
            pb="xs"
            style={{
              flexShrink: 0,
              borderBottom: '1px solid var(--mantine-color-default-border)',
            }}
          >
            <Group gap="md" wrap="nowrap" style={{ minWidth: 0 }}>
              <Text fw={600} size="lg">
                Task Details
              </Text>
              {showTrackTask ? (
                <TrackTaskConfirmControl
                  canMutate={canMutate}
                  loading={trackTaskLoading}
                  disabled={isSaving}
                  onConfirm={() => {
                    if (!canMutate) return;
                    if (quantityDirty) {
                      notifications.show({
                        title: 'Save changes first',
                        message: 'Save UOM and quantity changes before tracking this task.',
                        color: 'yellow',
                      });
                      return;
                    }
                    void onTrackTask?.(draft.id);
                  }}
                />
              ) : null}
              {draft.critical ? (
                <Group gap={4} wrap="nowrap">
                  <IconAlertTriangle size={13} color="var(--mantine-color-red-6)" />
                  <Text size="xs" c="red" fw={600}>
                    Critical task
                  </Text>
                </Group>
              ) : null}
            </Group>
            <Group gap={8} wrap="nowrap">
              {onSwitchToRightPanel ? (
                <Button
                  size="xs"
                  variant="subtle"
                  disabled={isSaving}
                  onClick={onSwitchToRightPanel}
                >
                  Switch to Right panel
                </Button>
              ) : null}
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={handleClose}
                disabled={isSaving}
                aria-label="Close"
              >
                <IconX size={18} />
              </ActionIcon>
            </Group>
          </Group>

          <Box
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <TaskModalBusyOverlay
              busy={isSaving}
              label="Saving changes.."
              style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
            >
              <Box
                style={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Box
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    padding: 'var(--mantine-spacing-md)',
                  }}
                >
                  <TaskDetailsFormBody
                    taskIdDisplay={draft.id}
                    draft={draft}
                    readOnlyForNonLeaf={readOnlyForNonLeaf}
                    resolvedLocationValue={resolvedLocationValue}
                    draftPredecessors={draftPredecessors}
                    successorText={successorText}
                    predecessorFieldLabel={predecessorFieldLabel}
                    successorFieldLabel={successorFieldLabel}
                    updateDraftField={updateDraftField}
                    updateDraftQuantity={updateDraftQuantity}
                    quantityErrors={quantityErrors}
                    getTask={(id) => store.getTask(id)}
                    updateDraftPredecessors={updateDraftPredecessors}
                    commitDraftPredecessors={commitDraftPredecessors}
                    compareBaselineIndex={compareBaselineIndex}
                    scrollToLinkedIssuesRequest={scrollToLinkedIssuesRequest}
                    onPickPredecessors={onPickPredecessors}
                    onOpenAssignUsers={onOpenAssignUsers}
                    assigneesSummary={assigneesSummary}
                  />
                </Box>
                <TaskDetailsFormActions
                  isDirty={isDirty}
                  saving={isSaving}
                  onSave={saveChanges}
                  onCancel={cancelChanges}
                  canMutate={canMutate}
                />
              </Box>
            </TaskModalBusyOverlay>
          </Box>
        </Paper>
      )}
    </Transition>
  );
};

export default TaskPanel;
